import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import {
  CLINIC_ADDRESS_LINE_1,
  CLINIC_ADDRESS_LINE_2,
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  PHONE_NUMBER,
  PHONE_TEL,
} from '@/config/site';

const LOGO_URL = "/images/brand/sdl-logo-dark.png";

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-charcoal text-warm-white pb-24 md:pb-8">
      <div className="container-luxury py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Specialist Dental London"
              className="h-12 w-auto brightness-0 invert mb-6"
            />
            <p className="font-body text-stone-400 text-sm leading-relaxed mb-6">
              Specialist-led dentistry in the heart of London. 
              Combining clinical excellence with genuine care.
            </p>
            <div className="flex items-center gap-2 text-accent-gold">
              <span className="font-accent text-xs tracking-widest uppercase">GDC Registered</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-6">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/services" data-testid="footer-link-services" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Our Services
              </Link>
              <Link to="/referrals" data-testid="footer-link-referrals" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Specialist Referrals
              </Link>
              <Link to="/team" data-testid="footer-link-team" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Meet the Team
              </Link>
              <Link to="/before-after" data-testid="footer-link-results" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Results Gallery
              </Link>
              <Link to="/contact" data-testid="footer-link-contact" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Contact Us
              </Link>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg mb-6">Specialist Services</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/services#implants" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Dental Implants
              </Link>
              <Link to="/services#full-arch" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Full Arch Reconstruction
              </Link>
              <Link to="/services#bone-grafting" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Bone Grafting
              </Link>
              <Link to="/services#sinus-lifting" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Sinus Lifting
              </Link>
              <Link to="/services#surgical-extractions" className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                Surgical Extractions
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg mb-6">Visit Us</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-accent-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-body text-sm text-stone-400">
                    {CLINIC_ADDRESS_LINE_1}<br />
                    {CLINIC_ADDRESS_LINE_2}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-accent-gold flex-shrink-0" strokeWidth={1.5} />
                <a href={PHONE_TEL} className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                  {PHONE_NUMBER}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-accent-gold flex-shrink-0" strokeWidth={1.5} />
                <a href={CONTACT_MAILTO} className="font-body text-sm text-stone-400 hover:text-warm-white transition-colors">
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-accent-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-body text-sm text-stone-400">
                    Mon - Fri: 9:00 - 18:00<br />
                    Sat: By appointment
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-stone-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-xs text-stone-500">
              © {new Date().getFullYear()} Specialist Dental London. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 md:justify-end">
              <Link to="/privacy" className="font-body text-xs text-stone-500 hover:text-stone-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/data-protection" className="font-body text-xs text-stone-500 hover:text-stone-400 transition-colors">
                Data Protection
              </Link>
              <Link to="/complaints-policy" className="font-body text-xs text-stone-500 hover:text-stone-400 transition-colors">
                Complaints Policy
              </Link>
              <Link to="/terms" className="font-body text-xs text-stone-500 hover:text-stone-400 transition-colors">
                Terms &amp; Conditions
              </Link>
              <Link to="/cookie-policy" className="font-body text-xs text-stone-500 hover:text-stone-400 transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
          <p className="font-body text-xs text-stone-600 mt-4 text-center md:text-left">
            This website does not provide medical advice. Content is for informational purposes only. 
            Always consult a qualified dental professional for diagnosis and treatment.
          </p>
        </div>
      </div>
    </footer>
  );
}
